import React from 'react';
import { useTranslation } from '../../providers/TranslationProvider';
import { ConfirmDialog } from './ConfirmDialog';

export interface DeleteConfirmationModalProps {
	/** Whether the modal is open */
	isOpen: boolean;
	/** Callback when the modal is closed (via cancel or close button) */
	onClose: () => void;
	/** Callback when delete is confirmed */
	onConfirm: () => void;
	/** Whether the delete operation is in progress */
	isDeleting?: boolean;
	/** The name of the item being deleted (for display in message) */
	itemName?: string;
	/** The type of item being deleted (e.g., "vendor", "item") */
	itemType?: string;
	/** Custom title for the modal */
	title?: string;
	/** Custom message for the modal */
	message?: string;
}

/**
 * A reusable delete confirmation modal. Thin wrapper around `ConfirmDialog`
 * that supplies delete-specific defaults (title, button label, danger variant)
 * and convenience props (`itemName`, `itemType`) for auto-generated messages.
 *
 * @example Basic usage
 * ```tsx
 * <DeleteConfirmationModal
 *   isOpen={isDeleteModalOpen}
 *   onClose={closeDelete}
 *   onConfirm={confirmDelete}
 *   isDeleting={isDeleting}
 *   itemName={deletingItem?.name}
 *   itemType="vendor"
 * />
 * ```
 */
export function DeleteConfirmationModal({
	isOpen,
	onClose,
	onConfirm,
	isDeleting = false,
	itemName,
	itemType = 'item',
	title,
	message,
}: DeleteConfirmationModalProps): React.ReactElement {
	const { t } = useTranslation();

	const displayTitle = title ?? t('Confirm Delete');

	const displayMessage =
		message ??
		(itemName != null
			? t(
					`Are you sure you want to delete "${itemName}"? This action cannot be undone.`,
				)
			: t(
					`Are you sure you want to delete this ${itemType}? This action cannot be undone.`,
				));

	return (
		<ConfirmDialog
			isOpen={isOpen}
			title={displayTitle}
			message={displayMessage}
			confirmText={isDeleting ? t('Deleting...') : t('Delete')}
			variant="danger"
			isProcessing={isDeleting}
			onConfirm={onConfirm}
			onCancel={onClose}
		/>
	);
}
